import { datadogLogs, type LogsInitConfiguration } from '@datadog/browser-logs';
import { datadogRum, type RumInitConfiguration } from '@datadog/browser-rum';
import { noop } from 'lodash-es';
import { readCookie } from '../utils/cookies';
import { NETWORK_ERROR_FINGERPRINT } from '../utils/http';

export interface DatadogArgs {
  environment: string;
  version: string;
  applicationId: string;
  clientToken: string;
  service: string;
  proxy?: string;

  /**
   * Percentage of ingested sessions profiled with the browser JS Self-Profiling API.
   * Omit or pass 0 to disable profiling (apps opt in by passing a rate — see {@link getProfilingSampleRate}).
   *
   * Requires the document to be served with a `Document-Policy: js-profiling` response header,
   * otherwise the profiler silently no-ops. See the app's nginx config.
   * See https://docs.datadoghq.com/real_user_monitoring/correlate_with_other_telemetry/profiling/?platform=browser
   */
  profilingSampleRate?: number;

  /**
   * If true, will require a manual call to `startSessionReplayRecording()`, possibly with `{ force: true }`,
   * depending on the value of `sessionReplaySampleRate`
   *
   * See https://github.com/DataDog/browser-sdk/issues/1967#issuecomment-2023862956
   */
  startSessionReplayRecordingManually?: boolean;
}

const CSP_ERROR_TYPES: Set<string> = new Set([
  'script-src',
  'font-src',
  'frame-src',
  'img-src',
  'script-src-elem',
  'media-src',
  'connect-src',
  'worder-src',
]);

export const JS_PROFILING_COOKIE_NAME = 'enable_js_profiling';

function hasCookie(name: string): boolean {
  return typeof document !== 'undefined' && readCookie(name) != null;
}

/**
 * Browser profiling sample rate (percentage of ingested sessions profiled) by environment:
 * - `staging` / `alpha` / `uat`: 100 — always profile lower environments
 * - `enable_js_profiling` cookie-enabled sessions: 100
 * - `talos_admin_organization_<env>` cookie-enabled sessions: 100 — Talos users morphed into another org
 * - everything else: 0
 */
export function getProfilingSampleRate(environment: string): number {
  if (environment === 'staging' || environment === 'alpha' || environment === 'uat') {
    return 100;
  }
  if (hasCookie(JS_PROFILING_COOKIE_NAME) || hasCookie(`talos_admin_organization_${environment}`)) {
    return 100;
  }
  return 0;
}

export function initDatadog({
  environment,
  version,
  applicationId,
  clientToken,
  service,
  proxy,
  profilingSampleRate = 0,
  startSessionReplayRecordingManually,
}: DatadogArgs) {
  const isLocal = environment === 'local';
  const isProduction = environment === 'prod' || environment === 'sandbox';

  const commonConfiguration = {
    applicationId,
    site: 'datadoghq.com',
    clientToken,
    service,
    proxy: proxy == null ? undefined : options => `${proxy}${options.path}?${options.parameters}`,
    env: environment,
    version,
    useSecureSessionCookie: !isLocal,
    telemetrySampleRate: 0,
    sessionSampleRate: 100,
  } as const satisfies RumInitConfiguration & LogsInitConfiguration;

  datadogRum.init({
    ...commonConfiguration,
    compressIntakeRequests: true,
    allowFallbackToLocalStorage: true,

    // Track all the things
    trackUserInteractions: true,
    trackLongTasks: true,
    trackResources: true,

    // Browser profiling (JS Self-Profiling API). Off unless the app passes a rate; 0 also disables.
    profilingSampleRate,

    // Only record session replays in production (100% of sessions by default)
    sessionReplaySampleRate: isProduction ? 100 : 0,
    startSessionReplayRecordingManually,
    beforeSend: (event, context) => {
      try {
        if (isLocal) {
          let consoleMethod: (message?: any, ...optionalParams: any[]) => void;
          switch (event.type) {
            case 'long_task':
            case 'vital':
            case 'resource':
            case 'view':
            case 'action':
              // These events are very noisy and low-value in local development, so we ignore them entirely instead of logging them to the console.
              consoleMethod = noop;
              break;
            case 'error':
              // Don't change this to console.error, it will cause an infinite loop with the blotter tabs. We need to find out why first.
              consoleMethod = console.warn;
              break;
            default:
              // eslint-disable-next-line no-console
              consoleMethod = console.debug;
              break;
          }
          consoleMethod('[Datadog]', event.type, event);
          // View events cannot be discarded
          // See https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration
          if (event.type === 'view') {
            return true;
          }
          return false;
        }

        // If the page was discarded, we want to know about it
        // See https://developer.chrome.com/blog/memory-and-energy-saver-mode
        if (event.type === 'long_task' && 'wasDiscarded' in document && document.wasDiscarded) {
          event.context = { ...event.context, wasDiscarded: true };
        }

        if (event.type === 'error') {
          const isNetworkError = event.error.fingerprint === NETWORK_ERROR_FINGERPRINT;
          // for DD noise reduction, ignore network errors if we are in non-prod (backend goes down a lot), or if the the user is offline
          const ignoreNetworkError = isNetworkError && (!isProduction || event.connectivity?.status !== 'connected');

          if (ignoreNetworkError) {
            return false;
          }

          // Always ignore CSP errors
          if (CSP_ERROR_TYPES.has(event.error.type as string)) {
            return false;
          }

          // Attempt to ignore any errors coming from chrome extensions

          // error.stack might not always be present, so check "handling stack" as well.
          // these properties were observed to exist at runtime, the casing difference between handling_stack and handlingStack is intentional
          const stack =
            event.error?.stack ??
            event.error?.handling_stack ??
            (context as any)?.error?.stack ??
            (context as any)?.handlingStack;
          if (stack?.includes('chrome-extension://')) {
            return false;
          }
        }

        return true;
      } catch (e) {
        // if any of the logic above throws an error, still make sure we log this to DD by returning true.
        // console.error this instead of logger.error to avoid a potential infinite loop
        console.error(e);
        return true;
      }
    },
  });

  // Logs
  datadogLogs.init({
    ...commonConfiguration,
    forwardErrorsToLogs: !isLocal,

    // note that debug/info are manually forwarded in our logger
    forwardConsoleLogs: isLocal ? [] : ['error', 'warn', 'info'],
  });

  // Event handlers
  document.addEventListener('visibilitychange', () => {
    datadogRum.setGlobalContextProperty('document.visibilityState', document.visibilityState);
    datadogLogs.setGlobalContextProperty('document.visibilityState', document.visibilityState);
  });
}
